/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IGroup } from '@fluentui/react/lib/DetailsList';
import { IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IStackItemStyles, IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer, useLocalStore } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { TablePagination } from 'src/core/components/TablePagination';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { useTranslatorCapabilities } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { ResourceValidationResult, validateTranslatorResource } from 'src/languageStudio/core/utils/demosUtils';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { TranslationCommandBar } from 'src/translation/components/TranslationCommandBar';
import { TranslationHistoryTable } from 'src/translation/components/TranslationHistoryTable';
import { TranslationJobDetailsPanel } from 'src/translation/components/TranslationJobDetailsPanel';
import { useTranslationJobTracker } from 'src/translation/hooks/useTranslationJobTracker';
import { TranslationDocumentStore } from 'src/translation/stores/TranslationDocumentStore';
import { TranslationJobStore } from 'src/translation/stores/TranslationJobStore';

export type PanelSettings = {
    isOpen: boolean;
    selectedJobId: string;
};

const messages = defineMessages({
    pageTitle: { id: 'translation.translationHistoryPage.pageTitle', defaultMessage: 'Job history' },
    tabTitle: { id: 'translation.translationHistoryPage.tabTitle', defaultMessage: 'Translation Jobs History' },
    goToSettings: { id: 'translation.translationHistoryPage.goToSettings', defaultMessage: 'Go to Settings' },
    pageSubtitle: {
        id: 'translation.translationHistoryPage.pageSubtitle',
        defaultMessage: 'Check the status of your current and previous translation jobs. You can click on a file to download'
    }
});

const theme = getScopedTheme('TranslationHistoryPage');

export const failedIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: theme.failureColor, fontSize: '14px' }
});
export const warningIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: theme.warningColor, fontSize: '14px' }
});
export const successIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: theme.successColor, fontSize: '14px' }
});

const headerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { paddingLeft: '28px', paddingBottom: '20px', marginBottom: '10px' }
});
const tableWrapperStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { padding: ' 0 30px 30px 30px', height: '90%' }
});

const paginationStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { margin: '10px 0 !important' } });

const getResubmissionFiles = (documents: TranslationDocumentStore[]) => {
    const resubmissionDocs = documents
        .map(doc => doc.fileName)
        .filter((docName, i, arr) => arr.indexOf(docName) === i)
        .map(docName => ({
            name: docName,
            containerName: documents[0].sourceContainerName
        }));

    return resubmissionDocs;
};

const filterJobByDates = (job: TranslationJobStore, dateFilter: { startDate: Date; endDate: Date }) => {
    const startDate = new Date(dateFilter.startDate).setHours(0, 0, 0, 0);
    const endDate = new Date(dateFilter.endDate).setHours(0, 0, 0, 0);
    const jobCreatedDate = new Date(job.createdDateTimeUtc).setHours(0, 0, 0, 0);

    let isJobCreatedInDateRange = true;
    if (dateFilter.startDate && jobCreatedDate < startDate) {
        isJobCreatedInDateRange = false;
    }

    if (dateFilter.endDate && jobCreatedDate > endDate) {
        isJobCreatedInDateRange = false;
    }
    return isJobCreatedInDateRange;
};

const getTableGroupsAndItems = (translationJobItems: TranslationJobStore[]) => {
    const items: TranslationDocumentStore[] = [];
    const groups: IGroup[] = [];
    let groupStartIndex = 0;

    translationJobItems.forEach((translationJob, index) => {
        const translationDocumentJobs = [...translationJob.documentStoreHouse.items];

        if (translationJob.error && (translationJob.status === 'Failed' || translationJob.status === 'ValidationFailed')) {
            translationDocumentJobs.unshift(
                new TranslationDocumentStore(`translationJobError-${translationJob.id}`, {
                    sourcePath: translationJob.error,
                    createdDateTime: translationJob.createdDateTimeUtc,
                    status: translationJob.status
                })
            );
        }
        const groupCount = translationDocumentJobs?.length;

        groups.push({
            level: 0,
            isCollapsed: true,
            count: groupCount,
            data: {
                jobId: translationJob.id,
                createdDateTime: translationJob.createdDateTimeUtc,
                status: translationJob.status
            },
            key: `translationJob-${index}`,
            name: translationJob.id,
            startIndex: groupStartIndex
        });

        groupStartIndex += groupCount;

        items.push(...translationDocumentJobs);
    });

    return { items, groups };
};

export const TranslationHistoryPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const history = useHistory();

    const { subscriptionId, csAccountStore } = rootStore.azureStore.session;
    const accountTokens = rootStore.azureStore.session.userTokens;
    const [isValidResource, setIsValidResource] = React.useState<ResourceValidationResult>({ isValid: false });

    React.useEffect(() => {
        if (csAccountStore?.id) {
            setIsValidResource(validateTranslatorResource({ localize, resource: csAccountStore }));
        }
    }, [csAccountStore?.id]);

    const resubmitTranslationTracker = useProgressTracker({
        onComplete: () => {
            history.push(routeTable.translation.documentTranslationHistory());
            setCurrentPage(0);
            fetchTranslationJobs();
        }
    });
    const { translationJobStoreHouse } = rootStore;

    useTranslationJobTracker(translationJobStoreHouse, () => {
        if (isValidResource.isValid) {
            history.push(routeTable.translation.documentTranslationHistory());
        }
    });
    const storageAccountTracker = useProgressTracker();
    const azureStorageAccountsStoreHouse = useLocalStore<ResourceStoreHouse<AzureStorageAccountStore>>(createResourceStoreHouse);

    const { supportedLanguages, getSupportedLanguages } = useTranslatorCapabilities();
    useAsyncEffect(async () => {
        if (isValidResource.isValid) {
            getSupportedLanguages();
        }
    }, [isValidResource.isValid]);

    const [panelSettings, updatePanelSettings] = useUpdateState<PanelSettings>({ isOpen: false, selectedJobId: '' });
    const [expandedJobIds, setExpandedJobIds] = React.useState<string[]>([]);
    const [selectedJobId, setSelectedJobId] = React.useState<string>(null);
    const [dateFilter, setDateFilter] = useUpdateState<{ startDate: Date; endDate: Date }>({ startDate: null, endDate: null });

    const sortedJobs = orderBy(translationJobStoreHouse.items, (job: TranslationJobStore) => job.createdDateTimeUtc, ['desc']);
    const filteredJobs = sortedJobs.filter(job => filterJobByDates(job, dateFilter));

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination<TranslationJobStore>(filteredJobs, 5);

    const { groups, items } = getTableGroupsAndItems(pageItems);

    const mappedGroups = groups.map(group => ({
        ...group,
        isCollapsed: !expandedJobIds.includes(group.data.jobId)
    }));

    React.useEffect(() => {
        if (isValidResource.isValid) {
            fetchTranslationJobs();
        }
    }, [isValidResource.isValid]);

    React.useEffect(() => {
        if (sortedJobs && translationJobStoreHouse.tracker.isCompleted && !expandedJobIds.includes(sortedJobs[0]?.id)) {
            setExpandedJobIds([...expandedJobIds, sortedJobs[0]?.id]);
        }
    }, [translationJobStoreHouse.tracker.isCompleted]);

    React.useEffect(() => {
        if (translationJobStoreHouse.items.length && translationJobStoreHouse.tracker.isCompleted && isValidResource.isValid) {
            fetchTranslationDocumentsJobs(pageItems.filter(j => j.documentStoreHouse.tracker.state === 'notStarted').map(job => job.id));
        }
    }, [translationJobStoreHouse.tracker.isCompleted, currentPage, dateFilter.endDate, dateFilter.startDate, isValidResource.isValid]);

    React.useEffect(() => {
        if (isValidResource.isValid && subscriptionId) {
            fetchStorageAccounts();
        }
    }, [subscriptionId, accountTokens, isValidResource.isValid]);

    const fetchStorageAccounts = () => {
        dispatcher.dispatch('getAzureStorageAccountsBySubscriptionId', {
            toastData: {},
            id: subscriptionId,
            tracker: storageAccountTracker,
            localStore: azureStorageAccountsStoreHouse
        });
    };

    const fetchTranslationJobs = () => {
        dispatcher.dispatch('fetchTranslationJobs', {
            toastData: {}
        });
    };

    const fetchTranslationDocumentsJobs = (jobIds: string[]) =>
        dispatcher.dispatch('fetchTranslationDocumentsJobs', { jobIds, toastData: {} });

    const onHandleCancelJob = (jobId: string) => {
        dispatcher.dispatch('cancelTranslationJob', {
            toastData: {},
            jobId
        });
        setSelectedJobId(null);
    };

    const onHandleResubmitJob = (jobId: string) => {
        const documents = translationJobStoreHouse.items.find(job => job.id === jobId).documentStoreHouse.items;
        dispatcher.dispatch('translateDocuments', {
            targetLanguages: documents.map(doc => doc.targetLanguageCode).filter((lang, i, arr) => arr.indexOf(lang) === i),
            files: getResubmissionFiles(documents),
            glossaryFiles: [],
            isAllFiles: false,
            storageAccountName: documents[0].sourceStorageAccountName,
            destinationContainerName: null,
            downloadTranslatedFiles: false,
            resourceGroupName: azureStorageAccountsStoreHouse.items.find(s => s.name === documents[0].sourceStorageAccountName)
                ?.resourceGroupName,
            destinationFolder: '',
            customTranslatorModelId: '',
            sourceLanguage: LuisConstants.TranslationTool.AutoLanguageDetectionItemKey,
            tracker: resubmitTranslationTracker
        });

        setSelectedJobId(null);
    };

    return (
        <Page pageName="TranslationJobsHistory" title={localize(messages.tabTitle)}>
            <Stack tokens={{ childrenGap: 5 }} styles={headerStyles}>
                <Stack verticalAlign="center" tokens={{ childrenGap: 5 }}>
                    <EditableTitle title={localize(messages.pageTitle)} />
                </Stack>
                <LuisText>{localize(messages.pageSubtitle)}</LuisText>
            </Stack>
            <Stack styles={tableWrapperStyles}>
                <TranslationCommandBar
                    selectedJobId={selectedJobId}
                    sortedJobs={sortedJobs}
                    isStorageAccountsLoading={storageAccountTracker.isRunning || azureStorageAccountsStoreHouse.tracker.isRunning}
                    onEndDateFilter={endDate => setDateFilter({ endDate })}
                    onStartDateFilter={startDate => setDateFilter({ startDate })}
                    onHandleResubmitJob={onHandleResubmitJob}
                    onHandleCancelJob={onHandleCancelJob}
                />

                <TranslationHistoryTable
                    tableItems={items}
                    tableGroups={mappedGroups}
                    expandedJobsIds={expandedJobIds}
                    selectedJobId={selectedJobId}
                    supportedLanguages={supportedLanguages}
                    onChangeSelectedJobId={setSelectedJobId}
                    onChangePanelSettings={updatePanelSettings}
                    onChangeExpandedJobIds={setExpandedJobIds}
                    isLoading={isValidResource.isValid && !translationJobStoreHouse.tracker.isCompleted}
                />
                <TablePagination
                    pageCount={pageCount}
                    currentPage={currentPage}
                    containerStyles={paginationStyles}
                    onPageChange={p => setCurrentPage(p.selected)}
                />
            </Stack>

            <TranslationJobDetailsPanel
                isOpen={panelSettings.isOpen}
                onDismiss={() => updatePanelSettings({ isOpen: false })}
                translationJob={translationJobStoreHouse.findById(panelSettings.selectedJobId)}
                supportedLanguages={supportedLanguages}
                storageAccounts={azureStorageAccountsStoreHouse.items}
                isStorageAccountsLoading={azureStorageAccountsStoreHouse.tracker.isRunning || storageAccountTracker.isRunning}
            />
        </Page>
    );
});
